import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import "./App.css";

function App() {
  const [userInput, setUserInput] = useState({
    AAAAA: "",
    BBBB1: "",
    BBBB2: "",
    CCCCC: "",
    DDDD1: "",
    DDDD2: "",
    DDDD3: "",
    EEEEE: "",
  });

  const [mode, setMode] = useState("ShortDesc");
  const [option, setOption] = useState("1");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [sessionStart, setSessionStart] = useState(Date.now());

  const recaptchaRef = useRef();
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  // ✅ FIX: make handler stable + defined before useEffect (satisfies exhaustive-deps)
  const handleUnload = useCallback(async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    try {
      await axios.post(`${backendUrl}/log-passive-session`, { sessionStart });
    } catch (err) {
      console.error("Passive session logging failed", err);
    }
  }, [sessionStart]);

  useEffect(() => {
    setSessionStart(Date.now());
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [handleUnload]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserInput({ ...userInput, [name]: value });
  };

  const handleModeChange = (e) => {
    setMode(e.target.value);
  };

  const getCaptchaToken = (tokenFromWidget) => {
    return new Promise((resolve, reject) => {
      if (!tokenFromWidget) {
        return reject(new Error("Missing reCAPTCHA token"));
      }
      resolve(tokenFromWidget);
    });
  };

  const handleCaptchaChange = async (tokenFromWidget) => {
    try {
      const token = await getCaptchaToken(tokenFromWidget);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await axios.post(
        `${backendUrl}/generate-folder-structure`,
        {
          ...userInput,
          mode,
          option,
          sessionStart,
          sessionID: Math.floor(Math.random() * 1e9),
          token,
        },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${userInput.EEEEE}.zip`);
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Error generating folder:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
      setShowCaptcha(false);
      if (recaptchaRef.current) recaptchaRef.current.reset();
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    const assetFields = [
      "AAAAA",
      "BBBB1",
      "BBBB2",
      "CCCCC",
      "DDDD1",
      "DDDD2",
      "DDDD3",
    ];
    const hasAssets = assetFields.some(
      (field) => userInput[field].trim() !== ""
    );
    const eventFolderName = userInput.EEEEE.trim();

    if (!hasAssets) {
      setError("Enter at least one asset (MSN, Engine SN, etc.).");
      return;
    }

    if (!eventFolderName) {
      setError("Event Folder Name cannot be empty.");
      return;
    }

    if (
      userInput.BBBB1 &&
      userInput.BBBB2 &&
      userInput.BBBB1.trim() === userInput.BBBB2.trim()
    ) {
      setError("Engine 1 SN and Engine 2 SN cannot be the same.");
      return;
    }

    const gearFields = [userInput.DDDD1, userInput.DDDD2, userInput.DDDD3].filter(
      (v) => v.trim() !== ""
    );
    if (new Set(gearFields).size !== gearFields.length) {
      setError("NLG, LMLG, and RMLG SNs must be unique.");
      return;
    }

    setError("");
    setLoading(true);

    if (process.env.NODE_ENV === "development") {
      handleCaptchaChange("dev-bypass-token");
    } else {
      setShowCaptcha(true);
    }
  };

  const tooltips = {
    AAAAA: "Aircraft MSN Format: NNNNN (e.g. 2712)",
    BBBB1: "Engine 1 SN Format: NNNNN (e.g. 12345)",
    BBBB2: "Engine 2 SN Format: NNNNN (e.g. 54321)",
    CCCCC: "APU SN Format: NNNNN or X-NNNNN",
    DDDD1: "Nose Landing Gear SN Format: XXXXXXX-NLG (e.g. MDL123-NLG)",
    DDDD2: "Left Main Landing Gear SN Format: XXXXXXX-LMLG (e.g. MDL123-LMLG)",
    DDDD3: "Right Main Landing Gear SN Format: XXXXXXX-RMLG (e.g. MDL123-RMLG)",
    EEEEE:
      "Event name (50 Chars) Format: YYYY-MM-DD EventDescription - Operator (e.g. 2024-03-15 C Check - Air ABC)",
  };

  const fieldLabels = {
    AAAAA: "Aircraft MSN",
    BBBB1: "Engine 1 SN",
    BBBB2: "Engine 2 SN",
    CCCCC: "APU SN",
    DDDD1: "NLG SN",
    DDDD2: "LMLG SN",
    DDDD3: "RMLG SN",
    EEEEE: "Event Folder Name",
  };

  const maxLengths = {
    AAAAA: 10,
    BBBB1: 10,
    BBBB2: 10,
    CCCCC: 25,
    DDDD1: 25,
    DDDD2: 25,
    DDDD3: 25,
    EEEEE: 50,
  };

  return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <img src="/Logo 1.svg" alt="Avolon Logo" className="logo" />
          <h1>
            <span className="ats-grey">ATS</span> Upload Folder Generator
          </h1>
        </div>
      </header>

      <main className="main-content">
        <div className="card">
          <div className="card-label-container">
            <span className="card-label">
              Enter Assets (minimum 1) + Event Name:
            </span>
          </div>

          <form onSubmit={handleSubmit} className="form">
            {Object.entries(fieldLabels).map(([name, label]) => (
              <div className="input-group" key={name}>
                <label htmlFor={name}>{label}</label>
                <div className="input-container">
                  <input
                    type="text"
                    name={name}
                    value={userInput[name]}
                    onChange={handleInputChange}
                    maxLength={maxLengths[name]}
                  />
                  <div className="tooltip-icon">
                    ℹ️
                    <span className="tooltip-text">{tooltips[name]}</span>
                  </div>
                </div>
              </div>
            ))}

            {error && <p className="message error">{error}</p>}
            {success && <p className="message success">Success! Folder generated.</p>}

            {showCaptcha && process.env.NODE_ENV !== "development" && (
              <div className="recaptcha-container">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={siteKey}
                  onChange={handleCaptchaChange}
                />
              </div>
            )}

            <div className="folder-selection">
              <div className="folder-options" style={{ marginBottom: "1rem" }}>
                <div className="folder-selection-header">
                  <label>
                    <strong>Project Type:</strong>
                  </label>
                  <span className="icon-group">
                    <div className="tooltip-icon">
                      ℹ️
                      <span className="tooltip-text">
                        Use the drop down to generate only the typical folders
                        related to that type of project – this reduces 'blank'
                        folders to be deleted prior to upload.
                      </span>
                    </div>
                  </span>
                </div>
                <div className="dropdown-group">
                  <select
                    value={option}
                    onChange={(e) => setOption(e.target.value)}
                    style={{ width: "30ch" }}
                  >
                    <option value="1">1 - Master (All Folders)</option>
                    <option value="2">2 - Delivery</option>
                    <option value="3">3 - Desktop Audit</option>
                    <option value="4">4 - Mid-Term Audit</option>
                    <option value="5">5 - Remarketing Audit</option>
                    <option value="6">6 - Sales Assistance</option>
                    <option value="7">7 - Pre-Redelivery</option>
                    <option value="8">8 - Pre-Purchase</option>
                    <option value="9">9 - Airframe Maintenance</option>
                    <option value="10">10 - Landing Gear Overhaul</option>
                    <option value="11">11 - Engine Shop Visit</option>
                    <option value="12">12 - APU Shop Visit</option>
                    <option value="13">13 - Modification</option>
                    <option value="14">14 - Transition</option>
                    <option value="15">15 - Mid-Term Audit (Enhanced)</option>
                  </select>
                </div>
              </div>

              <div className="folder-selection-header">
                <label>
                  <strong>Folder Description Mode:</strong>
                </label>
              </div>
              <div className="radio-group">
                <label>
                  <input
                    type="radio"
                    name="mode"
                    value="ShortDesc"
                    checked={mode === "ShortDesc"}
                    onChange={handleModeChange}
                  />
                  Short Description
                </label>
                <label>
                  <input
                    type="radio"
                    name="mode"
                    value="NoDesc"
                    checked={mode === "NoDesc"}
                    onChange={handleModeChange}
                  />
                  No Description
                </label>
                <label>
                  <input
                    type="radio"
                    name="mode"
                    value="Full"
                    checked={mode === "Full"}
                    onChange={handleModeChange}
                  />
                  Full Description
                </label>
              </div>
            </div>

            <div className="button-group">
              <button type="submit" disabled={loading} className="btn btn-primary">
                {loading ? "Generating..." : "Generate Folder Structure"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setUserInput({
                    AAAAA: "",
                    BBBB1: "",
                    BBBB2: "",
                    CCCCC: "",
                    DDDD1: "",
                    DDDD2: "",
                    DDDD3: "",
                    EEEEE: "",
                  });
                  setError("");
                }}
                className="btn btn-secondary"
              >
                Reset
              </button>
            </div>
          </form>
        </div>

        <div className="footer">
          © 2025-2026{" "}
          <a href="https://avionium.com" target="_blank" rel="noopener noreferrer">
            Avionium
          </a>
          . All rights reserved. <span className="ats-grey">[Ver: 4.0]</span>
        </div>
      </main>
    </div>
  );
}

export default App;
